"use client";

import type { ComponentType } from "react";
import type { ActivitySeedId } from "@/db/seed-data/activities";
import { AnimalFlowDisplayPreview } from "./animal-flow-display-preview";
import { BadmintonDisplayPreview } from "./badminton-display-preview";
import { BasketballDisplayPreview } from "./basketball-display-preview";
import { BoulderingDisplayPreview } from "./bouldering-display-preview";
import { ClimbingDisplayPreview } from "./climbing-display-preview";
import { CricketDisplayPreview } from "./cricket-display-preview";
import { CyclingDisplayPreview } from "./cycling-display-preview";
import { DayRetreatsDisplayPreview } from "./day-retreats-display-preview";
import { FootballDisplayPreview } from "./football-display-preview";
import { GolfDisplayPreview } from "./golf-display-preview";
import { HIITDisplayPreview } from "./hiit-display-preview";
import { HotYogaDisplayPreview } from "./hot-yoga-display-preview";
import { HyroxDisplayPreview } from "./hyrox-display-preview";
import { MixedMartialArtsDisplayPreview } from "./mixed-martial-arts-display-preview";
import { NutritionDisplayPreview } from "./nutrition-display-preview";
import { PadelDisplayPreview } from "./padel-display-preview";
import { PickleballDisplayPreview } from "./pickleball-display-preview";
import { PilatesDisplayPreview } from "./pilates-display-preview";
import { RecoveryDisplayPreview } from "./recovery-display-preview";
import { RunningDisplayPreview } from "./running-display-preview";
import { ScubaDivingDisplayPreview } from "./scuba-diving-display-preview";
import { SnookerDisplayPreview } from "./snooker-display-preview";
import { SnorkelingDisplayPreview } from "./snorkeling-display-preview";
import { SquashDisplayPreview } from "./squash-display-preview";
import { StretchingDisplayPreview } from "./stretching-display-preview";
import { SwimmingDisplayPreview } from "./swimming-display-preview";
import { TableTennisDisplayPreview } from "./table-tennis-display-preview";
import { TennisDisplayPreview } from "./tennis-display-preview";
import { ThrowballDisplayPreview } from "./throwball-display-preview";
import { TrekkingDisplayPreview } from "./trekking-display-preview";
import { VolleyballDisplayPreview } from "./volleyball-display-preview";
import { WalkingDisplayPreview } from "./walking-display-preview";
import { WeightTrainingDisplayPreview } from "./weight-training-display-preview";
import { YogaDisplayPreview } from "./yoga-display-preview";

const normalizeActivityId = (activityId: string) =>
  activityId
    .toLowerCase()
    .replace(/[^a-z-]/g, "")
    .replace(/-/g, "");

export type ActivityDisplayPreviewProps = {
  activityId: string;
  data?: unknown;
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

type ActivityDisplayPreviewComponent = ComponentType<{
  data: unknown;
  participants: ActivityDisplayPreviewProps["participants"];
}>;

const DISPLAY_PREVIEW_COMPONENTS: Partial<
  Record<ActivitySeedId, ActivityDisplayPreviewComponent>
> = {
  badminton: BadmintonDisplayPreview as ActivityDisplayPreviewComponent,
  basketball: BasketballDisplayPreview as ActivityDisplayPreviewComponent,
  climbing: ClimbingDisplayPreview as ActivityDisplayPreviewComponent,
  cricket: CricketDisplayPreview as ActivityDisplayPreviewComponent,
  cycling: CyclingDisplayPreview as ActivityDisplayPreviewComponent,
  "day-retreats": DayRetreatsDisplayPreview as ActivityDisplayPreviewComponent,
  football: FootballDisplayPreview as ActivityDisplayPreviewComponent,
  golf: GolfDisplayPreview as ActivityDisplayPreviewComponent,
  hyrox: HyroxDisplayPreview as ActivityDisplayPreviewComponent,
  "mixed-martial-arts":
    MixedMartialArtsDisplayPreview as ActivityDisplayPreviewComponent,
  padel: PadelDisplayPreview as ActivityDisplayPreviewComponent,
  pickleball: PickleballDisplayPreview as ActivityDisplayPreviewComponent,
  pilates: PilatesDisplayPreview as ActivityDisplayPreviewComponent,
  recovery: RecoveryDisplayPreview as ActivityDisplayPreviewComponent,
  running: RunningDisplayPreview as ActivityDisplayPreviewComponent,
  snooker: SnookerDisplayPreview as ActivityDisplayPreviewComponent,
  squash: SquashDisplayPreview as ActivityDisplayPreviewComponent,
  swimming: SwimmingDisplayPreview as ActivityDisplayPreviewComponent,
  "table-tennis": TableTennisDisplayPreview as ActivityDisplayPreviewComponent,
  tennis: TennisDisplayPreview as ActivityDisplayPreviewComponent,
  throwball: ThrowballDisplayPreview as ActivityDisplayPreviewComponent,
  volleyball: VolleyballDisplayPreview as ActivityDisplayPreviewComponent,
  walking: WalkingDisplayPreview as ActivityDisplayPreviewComponent,
  "weight-training":
    WeightTrainingDisplayPreview as ActivityDisplayPreviewComponent,
  yoga: YogaDisplayPreview as ActivityDisplayPreviewComponent,
  hiit: HIITDisplayPreview as ActivityDisplayPreviewComponent,
  "hot-yoga": HotYogaDisplayPreview as ActivityDisplayPreviewComponent,
  nutrition: NutritionDisplayPreview as ActivityDisplayPreviewComponent,
  "scuba-diving": ScubaDivingDisplayPreview as ActivityDisplayPreviewComponent,
  snorkeling: SnorkelingDisplayPreview as ActivityDisplayPreviewComponent,
  trekking: TrekkingDisplayPreview as ActivityDisplayPreviewComponent,
  "animal-flow": AnimalFlowDisplayPreview as ActivityDisplayPreviewComponent,
  bouldering: BoulderingDisplayPreview as ActivityDisplayPreviewComponent,
  stretching: StretchingDisplayPreview as ActivityDisplayPreviewComponent,
};

const DISPLAY_PREVIEW_ENTRIES = Object.entries(
  DISPLAY_PREVIEW_COMPONENTS,
) as Array<[ActivitySeedId, ActivityDisplayPreviewComponent]>;

/** Whether `activityId` has its own illustrated scene. */
export function hasActivityDisplayPreview(activityId: string) {
  const normalized = normalizeActivityId(activityId);
  return DISPLAY_PREVIEW_ENTRIES.some(
    ([supportedActivityId]) =>
      normalizeActivityId(supportedActivityId) === normalized,
  );
}

export function ActivityDisplayPreview({
  activityId,
  data = null,
  participants,
}: ActivityDisplayPreviewProps) {
  const normalized = normalizeActivityId(activityId);
  const PreviewComponent =
    DISPLAY_PREVIEW_ENTRIES.find(
      ([supportedActivityId]) =>
        normalizeActivityId(supportedActivityId) === normalized,
    )?.[1] ?? null;

  if (!PreviewComponent) {
    return null;
  }

  return <PreviewComponent data={data} participants={participants} />;
}
